import type { AnyItem, CompositePrimaryKey, ResolvedMonospaceCollection, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { isNullishOrEmpty } from '#layers/base/public';
import { getItemPrimaryKey, getRelationConstraint, getRelationPrimitiveFields, pickByPrimitiveFields } from '#layers/data-model/public';

export const createdItemKey = Symbol('createdItemKey');
export const originalItemKey = Symbol('originalItemKey');

export type CreateItem = AnyItem & { [createdItemKey]: string };

export function isCreatedItem(item: AnyItem) {
	return createdItemKey in (item ?? {});
}

export function getOriginalItemKey(item: AnyItem) {
	return item[originalItemKey];
}

export function markCreatedItem(item: AnyItem, originalItem: AnyItem) {
	return { ...item, [createdItemKey]: originalItem[createdItemKey] };
}

/**
 * Display-only marker (not persisted — Symbols drop out of serialization) flagging a
 * `_connect` to an already-existing target that should badge as "created" rather than
 * "connected" (e.g. a file uploaded in-form). The record already exists, so the junction
 * stays a `_connect`; only the badge changes.
 */
export const connectedAsCreatedKey = Symbol('connectedAsCreatedKey');

export function markConnectedAsCreated(item: AnyItem) {
	return { ...item, [connectedAsCreatedKey]: true };
}

export function isConnectedAsCreated(item: AnyItem) {
	return connectedAsCreatedKey in (item ?? {});
}

export function toReferencedItemKey(item: AnyItem, relationField: ResolvedMonospaceSingleRelationField) {
	const { constraint, thisSideConstrained } = getRelationConstraint(relationField);
	const { fields } = getRelationPrimitiveFields(relationField);

	if (!item || isNullishOrEmpty(fields)) return null;

	const itemKeys = pickByPrimitiveFields(item, fields);

	if (thisSideConstrained) {
		const mappedKeys: CompositePrimaryKey = {};

		for (const { constrainedField, referencedField } of constraint!.fieldMappings) {
			mappedKeys[referencedField!.apiName] = itemKeys[constrainedField!.apiName];
		}

		return mappedKeys;
	}

	return itemKeys;
}

export function fromReferencedItemKey(itemKey: CompositePrimaryKey, relationField: ResolvedMonospaceSingleRelationField) {
	const { constraint, thisSideConstrained } = getRelationConstraint(relationField);
	const { oppositeFields } = getRelationPrimitiveFields(relationField);

	// Backward relations update the constrained fields on the opposite item through the nested
	// operation. Their referenced fields live on the current item and must remain unchanged.
	if (!thisSideConstrained || !itemKey || isNullishOrEmpty(oppositeFields)) return null;

	const itemKeys = pickByPrimitiveFields(itemKey, oppositeFields);

	const mappedKeys: CompositePrimaryKey = {};

	for (const { constrainedField, referencedField } of constraint!.fieldMappings) {
		const field = constrainedField;
		const oppositeField = referencedField;
		if (!field || !oppositeField) continue;

		mappedKeys[field.apiName] = itemKeys[oppositeField.apiName];
	}

	return mappedKeys;
}

export function verifyItemKeys(originalKey: CompositePrimaryKey | null, payload: AnyItem, collection: ResolvedMonospaceCollection) {
	if ((!originalKey || Object.keys(originalKey).length === 0) && !payload?.[createdItemKey]) {
		throw new Error('The item is missing a createdItemKey');
	}

	if (originalKey) {
		const referenceKeys = Object.keys(getItemPrimaryKey(payload, collection));
		const hasAllKeys = referenceKeys.every((key) => Object.prototype.hasOwnProperty.call(originalKey, key));

		if (!hasAllKeys) {
			throw new Error('The item is missing a primary key');
		}
	}
}
